/**
 * Stage 2b chunk 2 — localStorage write-through to the cloud.
 *
 * The app's writes stay scattered across hooks/components and keep writing to
 * localStorage exactly as before. We install ONE wrapper around
 * window.localStorage.{setItem,removeItem,clear} that, for the keys listed in
 * SYNC_KEYS only, mirrors the change up to Supabase for the logged-in user —
 * debounced per key and fully fault-tolerant.
 *
 * Guarantees:
 *   - The original localStorage write always runs first and is never blocked.
 *   - Nothing here ever throws into the caller or the UI; cloud failures are
 *     swallowed (console.warn) so the app stays fully functional offline.
 *   - When there is no logged-in user, this is a no-op (localStorage only).
 *   - NO cloud reads / hydration happen here — this is push-only (chunk 3 reads).
 */
import { SYNC_KEYS, CLOUD_TABLES } from './syncKeys';
import {
  userPreferencesTable,
  PREFERENCE_CODECS,
  type AppPreferences,
} from './userPreferences';
import { strategyDraftsTable, type StrategyDraftsValue } from './strategyDrafts';
import { snapshotsTable } from './snapshots';

/** Per-key debounce window: rapid edits to one key collapse into one cloud write. */
const DEBOUNCE_MS = 1500;

// ── Current user ─────────────────────────────────────────────────────────────
// Set by AuthContext as the session resolves/changes. While null, sync is off.
let currentUserId: string | null = null;

/** Point the write-through at the logged-in user (or null to disable sync). */
export function setSyncUser(userId: string | null): void {
  currentUserId = userId;
}

// Original (unwrapped) mutators, captured at install. The login reconcile uses
// these to hydrate localStorage from the cloud WITHOUT re-triggering a push back
// up (the value just came FROM the cloud — no need to send it straight back).
let origSetItem: ((key: string, value: string) => void) | null = null;
let origRemoveItem: ((key: string) => void) | null = null;

/** Write to localStorage WITHOUT triggering the cloud push-back. Falls back to
 *  live localStorage if the wrapper isn't installed yet. Never throws. */
export function rawSetItem(key: string, value: string): void {
  try {
    if (origSetItem) origSetItem(key, value);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/** Remove a localStorage key WITHOUT triggering the cloud push-back. Never throws. */
export function rawRemoveItem(key: string): void {
  try {
    if (origRemoveItem) origRemoveItem(key);
    else window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Immediately push the current localStorage value of `key` up to the cloud
 * (the "seed local -> cloud" half of the login reconcile), bypassing the
 * per-key debounce. Reuses the same flush logic as the write-through, so it is a
 * no-op when logged out / the key isn't synced, and it never throws.
 */
export async function pushKeyNow(key: string): Promise<void> {
  await flush(key);
}

// ── Raw reads (getItem is never wrapped, so this is the real value) ──────────
function rawGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function readJsonArray(key: string): any[] {
  try {
    const raw = rawGetItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readJson(key: string): any {
  try {
    const raw = rawGetItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ── Per-key debounce ─────────────────────────────────────────────────────────
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleFlush(key: string): void {
  const existing = timers.get(key);
  if (existing) clearTimeout(existing);
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      void flush(key);
    }, DEBOUNCE_MS)
  );
}

/**
 * Cancel every pending debounced cloud push. Called on logout BEFORE clearing
 * local data: a queued flush would otherwise fire after the clear, read the now
 * empty localStorage, and (via removeNotIn with no keepIds) delete all of the
 * user's cloud rows.
 */
export function cancelPendingSync(): void {
  Array.from(timers.values()).forEach((timer) => clearTimeout(timer));
  timers.clear();
}

/** Called for every localStorage write; ignores non-synced keys and logged-out. */
function onKeyChanged(key: string): void {
  if (!SYNC_KEYS[key]) return; // not a synced key — leave it alone
  if (!currentUserId) return; // logged out — localStorage only, no cloud
  scheduleFlush(key);
}

/** localStorage.clear() wiped everything — reconcile every synced key to empty. */
function onCleared(): void {
  if (!currentUserId) return;
  for (const key of Object.keys(SYNC_KEYS)) scheduleFlush(key);
}

// ── Flush: mirror the current localStorage state of `key` to its cloud table ──
async function flush(key: string): Promise<void> {
  const userId = currentUserId;
  if (!userId) return;
  const descriptor = SYNC_KEYS[key];
  if (!descriptor) return;

  try {
    switch (descriptor.shape) {
      case 'multi':
        await syncMultiTable(key, descriptor.table, userId);
        break;
      case 'snapshots':
        await syncSnapshots(key, userId);
        break;
      case 'draft':
        // Any of the 3 draft keys rebuilds and upserts the single drafts row.
        await syncStrategyDrafts(userId);
        break;
      case 'preference':
        // Any of the 8 preference keys rebuilds and upserts the single prefs row.
        await syncPreferences(userId);
        break;
    }
  } catch (err) {
    // Never surface to the UI — log and carry on. App stays fully functional.
    // eslint-disable-next-line no-console
    console.warn(`[cloud-sync] sync failed for "${key}"`, err);
  }
}

/**
 * Array-backed table: make the cloud table match the current localStorage array.
 * Upsert ALL current rows first, THEN prune rows whose id is gone — so a failure
 * never leaves the user's cloud data wiped (no delete-all-then-insert).
 */
async function syncMultiTable(key: string, table: string, userId: string): Promise<void> {
  const items = readJsonArray(key);
  // CLOUD_TABLES is a heterogeneous registry; cast at this dispatch boundary.
  const api = CLOUD_TABLES[table as keyof typeof CLOUD_TABLES] as any;
  const rows = items.map((item) => api.toRow(item, userId));
  await api.upsertMany(rows);
  const keepIds = items
    .map((item) => (item && item.id != null ? String(item.id) : ''))
    .filter((id: string) => id !== '');
  await api.removeNotIn(userId, keepIds);
}

/** portfolio_snapshots: upsert all current (date,value) rows, then prune by date. */
async function syncSnapshots(key: string, userId: string): Promise<void> {
  const items = readJsonArray(key) as Array<{ date: string; value: number }>;
  const rows = items.map((s) => snapshotsTable.toRow(s, userId));
  await snapshotsTable.upsertMany(rows);
  const keepDates = items.map((s) => s.date).filter((d): d is string => !!d);
  await snapshotsTable.removeNotInDates(userId, keepDates);
}

/** Rebuild the single strategy_drafts row from the 3 draft keys' current values. */
async function syncStrategyDrafts(userId: string): Promise<void> {
  const value: StrategyDraftsValue = { buyPlan: null, sellPlan: null, taxHarvest: null };
  for (const [k, d] of Object.entries(SYNC_KEYS)) {
    if (d.shape !== 'draft') continue;
    (value as any)[d.field] = readJson(k);
  }
  await strategyDraftsTable.upsert(userId, value);
}

/** Rebuild the single user_preferences blob from all 8 preference keys. */
async function syncPreferences(userId: string): Promise<void> {
  const blob: AppPreferences = {};
  for (const [k, d] of Object.entries(SYNC_KEYS)) {
    if (d.shape !== 'preference') continue;
    const codec = PREFERENCE_CODECS[k];
    if (!codec) continue;
    const encoded = codec.encode(rawGetItem(k));
    if (encoded !== undefined) (blob as any)[d.prefKey] = encoded;
  }
  await userPreferencesTable.upsert(userId, blob);
}

// ── One-time install ─────────────────────────────────────────────────────────
let installed = false;

/**
 * Wrap window.localStorage's mutators once so synced-key writes mirror to the
 * cloud. Idempotent (guards StrictMode double-mount / re-renders). The original
 * write always runs first; the cloud trigger is best-effort and never throws.
 */
export function installWriteThrough(): void {
  if (installed) return;
  if (typeof window === 'undefined' || !window.localStorage) return;
  installed = true; // set before patching so a re-entrant call can't double-wrap

  const ls = window.localStorage;
  try {
    const origSet = ls.setItem.bind(ls);
    const origRemove = ls.removeItem.bind(ls);
    const origClear = ls.clear.bind(ls);

    // Expose the unwrapped mutators so the reconcile can hydrate without echo.
    origSetItem = origSet;
    origRemoveItem = origRemove;

    ls.setItem = (k: string, v: string) => {
      origSet(k, v);
      try {
        onKeyChanged(k);
      } catch {
        /* never let the cloud trigger break a localStorage write */
      }
    };
    ls.removeItem = (k: string) => {
      origRemove(k);
      try {
        onKeyChanged(k);
      } catch {
        /* ignore */
      }
    };
    ls.clear = () => {
      origClear();
      try {
        onCleared();
      } catch {
        /* ignore */
      }
    };
  } catch (err) {
    // Some environments make these non-writable; degrade to localStorage-only.
    // eslint-disable-next-line no-console
    console.warn('[cloud-sync] could not install localStorage write-through', err);
  }
}
